/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { OverflowCarousel } from '@coursera-web/cds-labs';

import { Skeleton, breakpoints, useMediaQuery } from '@coursera/cds-core';

import AiSearchCoachButton from 'bundles/front-page/redesign-modules/ai-search/AiSearchCoachButton';
import { PROMPTS_DISPLAY_LIMIT_LOHP_PILLS } from 'bundles/search-common/constants/autocomplete';
import { useQueryPrompts } from 'bundles/search-common/providers/QueryPromptsProvider';

const styles = {
  container: css`
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: var(--cds-spacing-150);
    padding: var(--cds-spacing-25) 0;
  `,
  wrapPills: css`
    flex-wrap: wrap;
  `,
  nowrapPills: css`
    flex-wrap: nowrap;
  `,
  pillSkeleton: css`
    transform: scale(1, 1) !important;
    border-radius: 20px !important;
  `,
  // Single scrollable viewport — both rows scroll together
  mobileScrollContainer: css`
    overflow-x: auto;
    position: relative;
    scrollbar-width: none;
    -ms-overflow-style: none;
    padding: var(--cds-spacing-25) 0;
    margin-left: calc(var(--cds-spacing-200) * -1);
    margin-right: calc(var(--cds-spacing-200) * -1);

    &::-webkit-scrollbar {
      display: none;
    }
  `,
  // Expands to the natural width of the widest row so the scroll container can clip it
  mobileInnerContainer: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-150);
    width: max-content;
    margin: 0 var(--cds-spacing-200);
  `,
  mobileRow: css`
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: var(--cds-spacing-150);
  `,
  mobileLoadingStateContainer: css`
    display: none;
    flex-direction: column;
    gap: var(--cds-spacing-150);

    ${breakpoints.down('sm')} {
      display: flex;
    }
  `,
  desktopLoadingStateContainer: css`
    display: block;
    ${breakpoints.down('sm')} {
      display: none;
    }
  `,
};

const HALF_PROMPTS_DISPLAY_LIMIT_LOHP_PILLS = Math.floor(PROMPTS_DISPLAY_LIMIT_LOHP_PILLS / 2);

const AiPromptPillsContainer = () => {
  const { prompts, loading } = useQueryPrompts();
  const isMobile = useMediaQuery(breakpoints.down('sm'));

  const getRandomWidthOfPillSkeleton = (min: number, max: number) => {
    return Math.floor(Math.random() * (max - min + 1)) + min;
  };

  const renderPills = ({ start = 0, end }: { start?: number; end?: number }) => {
    return prompts?.slice(start, end)?.map((prompt) => <AiSearchCoachButton key={prompt.id} prompt={prompt} />);
  };

  const renderLoadingStatePills = (length: number) => {
    return Array.from({ length }).map((_, index) => (
      <Skeleton
        // eslint-disable-next-line react/no-array-index-key
        key={`pill-skeleton-${index}`}
        width={getRandomWidthOfPillSkeleton(180, 280)}
        height={32}
        css={styles.pillSkeleton}
      />
    ));
  };

  if (loading) {
    return (
      <>
        <div css={styles.mobileLoadingStateContainer}>
          <div css={styles.mobileScrollContainer}>
            <div css={styles.mobileInnerContainer}>
              <div css={styles.mobileRow}>{renderLoadingStatePills(HALF_PROMPTS_DISPLAY_LIMIT_LOHP_PILLS)}</div>
              <div css={styles.mobileRow}>{renderLoadingStatePills(HALF_PROMPTS_DISPLAY_LIMIT_LOHP_PILLS)}</div>
            </div>
          </div>
        </div>
        <div css={styles.desktopLoadingStateContainer}>
          <OverflowCarousel css={[styles.container, styles.wrapPills]}>
            {renderLoadingStatePills(PROMPTS_DISPLAY_LIMIT_LOHP_PILLS)}
          </OverflowCarousel>
        </div>
      </>
    );
  }

  if (!loading && !prompts?.length) {
    return null;
  }

  return (
    <>
      {isMobile ? (
        <div css={styles.mobileScrollContainer}>
          <div css={styles.mobileInnerContainer}>
            <div css={styles.mobileRow}>{renderPills({ start: 0, end: HALF_PROMPTS_DISPLAY_LIMIT_LOHP_PILLS })}</div>
            <div css={styles.mobileRow}>
              {renderPills({ start: HALF_PROMPTS_DISPLAY_LIMIT_LOHP_PILLS, end: PROMPTS_DISPLAY_LIMIT_LOHP_PILLS })}
              <AiSearchCoachButton isAskAnything />
            </div>
          </div>
        </div>
      ) : (
        <OverflowCarousel css={[styles.container, styles.wrapPills]}>
          {renderPills({ start: 0, end: PROMPTS_DISPLAY_LIMIT_LOHP_PILLS })}
          <AiSearchCoachButton isAskAnything />
        </OverflowCarousel>
      )}
    </>
  );
};

export default AiPromptPillsContainer;
